import { type GetStaticProps } from 'next'
import Head from 'next/head'
import React from 'react'
import { SWRConfig } from 'swr'

import { PUBLIC_THERAPISTS_PAGE_URL } from '@/legacy-src/constants/locale.constants'
import { useCanonicalUrl } from '@/legacy-src/hooks/useCanonicalUrl'
import { USE_THERAPISTS_SWR_KEY } from '@/legacy-src/hooks/useTherapists'

import { fetchCMSMenu, getCMSMenuSWRKey } from '@/src/domains/cms-menus/services/fetchCMSMenu'
import {
  fetchHeaderServicesPages,
  getHeaderServicesPagesSWRKey,
} from '@/src/domains/header/services/fetchHeaderServicesPages'

import { fetchFromStrapiRest } from '../lib/strapi/fetchFunctions'
import {
  fetchFromApi,
  fetchProcessedTherapists,
  fetchUseServices,
  fetchUseSpecializations,
} from '../utilities/apiUtils'
import { optimizeTherapistData, unOptimizeTherapistData } from '../utilities/optimizeData'
import ServicePage from './servicePage/ServicePage'

const TherapistPublicListPage = ({ cmsData, useTherapistsOptimized, ...props }) => {
  const canonicalUrl = useCanonicalUrl()
  const fallback = { ...props.fallback, [USE_THERAPISTS_SWR_KEY]: unOptimizeTherapistData(useTherapistsOptimized) }
  const PUBLIC_THERAPISTS_PAGE_URL_WITH_SERVICE = `${PUBLIC_THERAPISTS_PAGE_URL}?service=${props.fallback.useServices[0].id}`

  return (
    <SWRConfig
      value={{
        ...(fallback ? { fallback } : {}),
      }}
    >
      <Head>
        <title>{props.t('headers.therapists')}</title>
        <link href={canonicalUrl} rel="canonical" />
        <meta content={props.t('meta.description')} name="description" />
        <meta content={props.t('meta.title')} name="og:title" property="og:title" />
        <meta content={props.t('meta.description')} name="og:description" property="og:description" />
        <meta content={PUBLIC_THERAPISTS_PAGE_URL_WITH_SERVICE} property="og:url" />
      </Head>
      <ServicePage cmsData={cmsData} />
    </SWRConfig>
  )
}

export const therapistPublicListGetStaticProps = (isPathValidForLocale: boolean) =>
  isPathValidForLocale
    ? ((async () => {
        const [
          useTherapists,
          useServices,
          useSpecializations,
          servicePages,
          useTherapistCategories,
          mainMenu,
          otherMenu,
          therapistMenu,
          clinicMenu,
        ] = await Promise.all([
          fetchProcessedTherapists(),
          fetchUseServices(),
          fetchUseSpecializations(),
          fetchHeaderServicesPages(),
          fetchFromApi('therapist/categories'),
          fetchCMSMenu('main-menu'),
          fetchCMSMenu('other-menu'),
          fetchCMSMenu('therapist-menu'),
          fetchCMSMenu('clinic-menu'),
        ])
        const cmsData = await fetchFromStrapiRest('service-pages', {
          populate: { content: { populate: '*' } },
        })

        const useTherapistsOptimized = optimizeTherapistData(useTherapists)

        return {
          props: {
            fallback: {
              useServices,
              useSpecializations,
              useTherapistCategories,
              [getHeaderServicesPagesSWRKey()]: servicePages,
              [getCMSMenuSWRKey('main-menu')]: mainMenu,
              [getCMSMenuSWRKey('other-menu')]: otherMenu,
              [getCMSMenuSWRKey('therapist-menu')]: therapistMenu,
              [getCMSMenuSWRKey('clinic-menu')]: clinicMenu,
            },
            cmsData,
            useTherapistsOptimized,
          },
          revalidate: 300,
        }
      }) satisfies GetStaticProps)
    : ((async () => {
        return { notFound: true }
      }) satisfies GetStaticProps)

export default TherapistPublicListPage
